import { Pencil } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import { Checkbox } from "@/marketing/components/ui/checkbox";
import {
  type InfluencerCampaign,
  STATUS_META,
  TRAFFIC_DOT,
} from "@/marketing/models/influencerCampaign";

import { formatDate, progressPct } from "@/marketing/lib/formatters";
import { useBrandLookup } from "@/marketing/components/brand/useBrandLookup";

function ProgressInline({
  current,
  total,
  color,
}: {
  current: number;
  total: number | undefined;
  color: string;
}) {
  const pct = progressPct(current, total);
  return (
    <div className="tw-flex tw-items-center tw-gap-1.5">
      <div className="tw-h-1 tw-w-14 tw-overflow-hidden tw-rounded-full tw-bg-muted">
        <div
          className={cn("tw-h-full tw-rounded-full", color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tw-text-[11px] tw-text-muted-foreground">
        {current}/{total ?? 0}
      </span>
    </div>
  );
}

interface CampaignTableViewProps {
  campaigns: InfluencerCampaign[];
  onRowClick: (campaign: InfluencerCampaign) => void;
  onRowEdit: (campaign: InfluencerCampaign) => void;
}

export function CampaignTableView({
  campaigns,
  onRowClick,
  onRowEdit,
}: CampaignTableViewProps) {
  const lookupBrand = useBrandLookup();
  return (
    <div className="tw-h-full tw-overflow-auto tw-rounded-lg tw-border tw-border-border">
      <table className="tw-w-full tw-text-sm">
        <thead className="tw-sticky tw-top-0 tw-bg-muted/50">
          <tr className="tw-text-left tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
            <th className="tw-w-8 tw-px-3 tw-py-2.5">
              <Checkbox />
            </th>
            <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Campaign</th>
            <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Brand</th>
            <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Status</th>
            <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Traffic</th>
            <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Owner</th>
            <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Onboarding</th>
            <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Posts</th>
            <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Due</th>
            <th className="tw-w-10 tw-px-3 tw-py-2.5"></th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => (
            <tr
              key={c.campaignId}
              className="tw-cursor-pointer tw-border-t tw-border-border hover:tw-bg-muted/30"
              onClick={() => onRowClick(c)}
            >
              <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
                <Checkbox />
              </td>
              <td className="tw-px-3 tw-py-2.5 tw-font-medium">{c.name}</td>
              <td className="tw-px-3 tw-py-2.5">
                <span className="tw-rounded-full tw-bg-muted tw-px-2 tw-py-0.5 tw-text-[11px]">
                  {lookupBrand(c.brandId)?.name ?? c.brandId.slice(0, 8)}
                </span>
              </td>
              <td className="tw-px-3 tw-py-2.5">
                <span
                  className={cn(
                    "tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium",
                    STATUS_META[c.status].pill,
                  )}
                >
                  {STATUS_META[c.status].label}
                </span>
              </td>
              <td className="tw-px-3 tw-py-2.5">
                {c.trafficLight && (
                  <span
                    className={cn(
                      "tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full",
                      TRAFFIC_DOT[c.trafficLight],
                    )}
                  />
                )}
              </td>
              <td className="tw-px-3 tw-py-2.5 tw-text-xs">{c.ownerId}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-xs">
                <ProgressInline
                  current={c.onboardedCount ?? 0}
                  total={c.onboardingTarget}
                  color="tw-bg-violet-600"
                />
              </td>
              <td className="tw-px-3 tw-py-2.5 tw-text-xs">
                <ProgressInline
                  current={c.postedCount ?? 0}
                  total={c.postTarget}
                  color="tw-bg-emerald-500"
                />
              </td>
              <td className="tw-px-3 tw-py-2.5 tw-text-xs">
                {formatDate(c.postingDueDate)}
              </td>
              <td
                className="tw-px-3 tw-py-2.5"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  aria-label="Edit campaign"
                  onClick={() => onRowEdit(c)}
                  className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-muted-foreground hover:tw-bg-accent hover:tw-text-foreground"
                >
                  <Pencil className="tw-h-3 tw-w-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
